import { useState } from 'react';

import { Check } from 'lucide-react';

import { cn, normalizeColorHex } from '@/lib/utils';
import { EntityAvatar } from '@/modules/common';
import { SingleSelectOption } from '@/modules/common/components/molecules/selectors/single-select/single-select-types';
import { ScrollableText } from '@/modules/planner/components/assignment/text/scrollable-text';

/**
 * Renderers for the SingleSelect used by the "Add custom line" pickers in the
 * generate-invoices and generate-bills pages. Members and projects can have
 * long names that the default `truncate` cell silently clips; here we wrap the
 * label in ScrollableText so the user can scrub the full name on hover.
 *
 * `showColorDot` is for the bills project picker, which also surfaces the
 * project's color hex next to the avatar (matching the pattern from other
 * project-name cells in the app).
 *
 * Hover detection note:
 *   ScrollableText's built-in onMouseEnter listener doesn't reliably fire when
 *   the component is rendered inside cmdk's CommandItem (the popover relies on
 *   pointer-move tracking on the list, which seems to swallow enter events on
 *   nested descendants). To work around this we track hover on the option
 *   container itself and drive ScrollableText via `externalHovered` — that
 *   path doesn't depend on the popover's pointer model.
 */

interface RendererConfig {
  showColorDot?: boolean;
}

interface ScrollableOptionProps {
  option: SingleSelectOption;
  isSelected: boolean;
  showColorDot: boolean;
}

const ScrollableOption = ({ option, isSelected, showColorDot }: ScrollableOptionProps) => {
  const [hovered, setHovered] = useState(false);
  const normalizedColor = normalizeColorHex(option.color);

  return (
    <div
      className={cn(
        'relative flex w-full cursor-default select-none items-center gap-3 rounded-sm py-1 pl-1 pr-8 text-sm outline-none',
        'hover:bg-accent hover:text-accent-foreground',
        'focus:bg-accent focus:text-accent-foreground',
        isSelected && 'bg-accent text-accent-foreground'
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="absolute right-2 flex h-4 w-4 items-center justify-center">
        <Check
          className={cn(
            'h-4 w-4 transition-opacity',
            isSelected ? 'text-primary opacity-100' : 'opacity-0'
          )}
        />
      </span>
      <EntityAvatar
        image={option.avatar}
        color={showColorDot ? undefined : normalizedColor}
        name={option.label}
        size="sm"
        shape={showColorDot ? 'rounded' : 'circle'}
        className="flex-shrink-0"
      />
      {showColorDot && normalizedColor && (
        <div
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: normalizedColor }}
          aria-hidden
        />
      )}
      <div className="min-w-0 flex-1 overflow-hidden">
        <ScrollableText
          className="whitespace-nowrap text-sm font-normal leading-tight"
          externalHovered={hovered}
        >
          {option.label}
        </ScrollableText>
      </div>
    </div>
  );
};

interface ScrollableTriggerProps {
  selectedOption: SingleSelectOption;
  showColorDot: boolean;
}

const ScrollableTrigger = ({ selectedOption, showColorDot }: ScrollableTriggerProps) => {
  const [hovered, setHovered] = useState(false);
  const normalizedColor = normalizeColorHex(selectedOption.color);

  return (
    <div
      className="flex min-w-0 flex-1 items-center gap-2"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <EntityAvatar
        image={selectedOption.avatar}
        color={showColorDot ? undefined : normalizedColor}
        name={selectedOption.label}
        size="sm"
        shape={showColorDot ? 'rounded' : 'circle'}
        className="!h-5 !w-5 flex-shrink-0"
      />
      {showColorDot && normalizedColor && (
        <div
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: normalizedColor }}
          aria-hidden
        />
      )}
      <div className="min-w-0 flex-1 overflow-hidden">
        <ScrollableText
          className="whitespace-nowrap text-sm font-normal leading-tight"
          externalHovered={hovered}
        >
          {selectedOption.label}
        </ScrollableText>
      </div>
    </div>
  );
};

export const createScrollableOptionRenderer = ({ showColorDot = false }: RendererConfig = {}) =>
  function renderScrollableOption(option: SingleSelectOption, isSelected: boolean) {
    return <ScrollableOption option={option} isSelected={isSelected} showColorDot={showColorDot} />;
  };

export const createScrollableTriggerRenderer = ({ showColorDot = false }: RendererConfig = {}) =>
  function renderScrollableTrigger(selectedOption: SingleSelectOption | null, placeholder: string) {
    if (!selectedOption) {
      return (
        <span className="truncate text-sm font-normal text-muted-foreground">{placeholder}</span>
      );
    }
    return <ScrollableTrigger selectedOption={selectedOption} showColorDot={showColorDot} />;
  };
